/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { productCatalog } from '../../features/products/data/products';
import { authService } from '../services/authService';
import { signInWithGooglePopup } from '../services/firebaseAuth';
import { cartService, type CartApiItem } from '../services/cartService';
import { orderService } from '../services/orderService';
import { paymentService } from '../services/paymentService';
import { directPaymentService } from '../services';
import type { DirectPodPaymentInput, DirectPaymentResult } from '../services/directPaymentService';
import { userService } from '../services/userService';
import { API_BASE_URL, BACKEND_BASE_URL } from '../config/api';
import { normalizeProductMediaType } from '../utils/productMedia';
import { parseCategoryString } from '../utils/categories';
import type {
  BuyerRegistrationInput,
  Category,
  CartItem,
  CheckoutPayload,
  Order,
  OrderStatus,
  Product,
  User,
  UserRole,
} from '../types/shop';
import type { UserData } from '../types/auth';

type ThemeMode = 'light' | 'dark';

interface ShopContextValue {
  currentUser: User | null;
  users: User[];
  products: Product[];
  categories: Category[];
  cart: CartItem[];
  orders: Order[];
  wishlist: string[];
  themeMode: ThemeMode;
  cartSubtotal: number;
  cartTotalItems: number;
  isLoading: boolean;
  isCatalogLoading: boolean;
  isUsersLoading: boolean;
  refreshCart: () => Promise<void>;
  refreshCatalog: () => Promise<void>;
  refreshUsers: () => Promise<void>;
  refreshOrders: () => Promise<void>;
  createProduct: () => Promise<Product | null>;
  updateProduct: () => Promise<Product | null>;
  deleteProduct: (productId: string) => Promise<void>;
  toggleWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  // Sync methods (local storage based - fallback)
  loginBuyer: (email: string, password: string) => void;
  loginVendor: (email: string, password: string) => void;
  registerBuyer: (payload: BuyerRegistrationInput) => void;
  loginWithSocial: () => void;
  loginAdmin: (email: string, password: string) => boolean;
  updateProfile: (updates: Partial<User>) => void;
  logout: () => void;
  // Async methods (API based)
  loginBuyerAsync: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  registerBuyerAsync: (payload: BuyerRegistrationInput) => Promise<{ success: boolean; message?: string }>;
  logoutAsync: () => Promise<void>;
  updateProfileAsync: (updates: Partial<User>) => Promise<{ success: boolean; message?: string }>;
  // Cart & Order methods
  addToCart: (product: Product, quantity: number, options?: { edition?: 'digital' | 'print' }) => void;
  updateCartQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  placeOrder: (payload: CheckoutPayload) => Promise<Order>;
  directPodPayment: (input: DirectPodPaymentInput) => Promise<DirectPaymentResult>;
  directPodPaymentToFlutterwave: (product: Product, podProjectId?: string) => Promise<DirectPaymentResult>;
  updateOrderStatus: (orderId: string, nextStatus: OrderStatus) => Promise<void>;
  toggleTheme: () => void;
}

const USER_STORAGE_KEY = 'haapa.currentUser';
const ORDER_STORAGE_KEY = 'haapa.orders';
const THEME_STORAGE_KEY = 'haapa.themeMode';
const BUYER_PROFILES_STORAGE_KEY = 'haapa.buyerProfiles';
const WISHLIST_STORAGE_KEY = 'haapa.wishlist';
const CART_STORAGE_KEY_PREFIX = 'haapa.cart.';

const ADMIN_EMAIL = 'admin@haapa.store';
const ADMIN_PASSWORD = 'Admin@123';

const noop = () => {};
const emptyContext: ShopContextValue = {
  currentUser: null,
  users: [],
  products: [],
  categories: [],
  cart: [],
  orders: [],
  wishlist: [],
  themeMode: 'light',
  loginBuyerAsync: async () => ({ success: false, message: 'ShopProvider not mounted' }),
  loginBuyer: noop,
  loginVendor: noop,
  registerBuyer: noop,
  loginWithSocial: noop,
  loginAdmin: () => false,
  updateProfile: noop,
  logoutAsync: async () => {},
  updateProfileAsync: async () => ({ success: false, message: 'ShopProvider not mounted' }),
  cartSubtotal: 0,
  cartTotalItems: 0,
  isUsersLoading: false,
  refreshUsers: async () => {},
  refreshOrders: async () => {},
  createProduct: async () => null,
  updateProduct: async () => null,
  deleteProduct: async () => {},
  toggleWishlist: noop,
  isInWishlist: () => false,
  registerBuyerAsync: async () => ({ success: false, message: 'ShopProvider not mounted' }),
  logout: () => {},
  addToCart: noop,
  updateCartQuantity: noop,
  removeFromCart: noop,
  placeOrder: async (): Promise<Order> => { throw new Error('ShopProvider not mounted'); },
  updateOrderStatus: async () => {},
  toggleTheme: noop,
  refreshCatalog: async () => {},
  isCatalogLoading: false,
  isLoading: false,
  refreshCart: async () => {},
};

const ShopContext = createContext<ShopContextValue | undefined>(emptyContext);

const readStorage = <T,>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const value = window.localStorage.getItem(key);
    if (!value) {
      return fallback;
    }

    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const buildOrderId = (): string => `ORD-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
const normalizeEmail = (email: string): string => email.trim().toLowerCase();
const getCartStorageKey = (user: User): string => {
  const email = typeof user.email === 'string' ? user.email.trim() : '';
  const identifier = email ? normalizeEmail(email) : user.id.trim();
  return `${CART_STORAGE_KEY_PREFIX}${identifier || 'anonymous'}`;
};
const normalizeRole = (role?: string): UserRole => {
  if (role === 'admin') {
    return 'admin';
  }
  if (role === 'manager' || role === 'staff') {
    return 'manager';
  }
  if (role === 'vendor' || role === 'creator') {
    return 'vendor';
  }

  return 'buyer';
};

const buildUsernameFromEmail = (email: string): string => {
  const [localPart = 'reader'] = normalizeEmail(email).split('@');
  const sanitized = localPart.replace(/[^a-zA-Z0-9._-]/g, '');
  return sanitized || `reader${Date.now().toString().slice(-5)}`;
};

const getAudienceProfileFromDateOfBirth = (
  dateOfBirth?: string,
): 'kid' | 'adult' | undefined => {
  if (!dateOfBirth) {
    return undefined;
  }

  const parsedDate = new Date(dateOfBirth);
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined;
  }

  const today = new Date();
  let age = today.getFullYear() - parsedDate.getFullYear();
  const monthDifference = today.getMonth() - parsedDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < parsedDate.getDate())) {
    age -= 1;
  }

  return age < 18 ? 'kid' : 'adult';
};

const buildBuyerNameFromEmail = (email: string): string => {
  const [namePart] = email.split('@');
  if (!namePart) {
    return 'Guest Buyer';
  }

  return namePart
    .split(/[._-]/g)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
};

const isPlaceholderProfileName = (value?: string): boolean => {
  const normalizedValue = value?.trim().toLowerCase();
  return normalizedValue === 'reader' || normalizedValue === 'guest buyer';
};

const resolveDisplayName = (input: {
  email?: string;
  name?: string;
  fullname?: string;
  fullName?: string;
  firstname?: string;
  firstName?: string;
  lastname?: string;
  lastName?: string;
  username?: string;
}): string => {
  const resolvedEmail = input.email ? normalizeEmail(input.email) : undefined;
  const resolvedFirstName = input.firstname?.trim() || input.firstName?.trim();
  const resolvedLastName = input.lastname?.trim() || input.lastName?.trim();
  const combinedName = [resolvedFirstName, resolvedLastName].filter(Boolean).join(' ');
  const explicitName = input.name?.trim() || input.fullname?.trim() || input.fullName?.trim();

  if (explicitName && !isPlaceholderProfileName(explicitName)) {
    return explicitName;
  }

  return combinedName || resolvedFirstName || input.username?.trim() || (resolvedEmail ? buildBuyerNameFromEmail(resolvedEmail) : 'Guest Buyer');
};

const normalizeUserAssetUrl = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (/^(https?:|data:|blob:)/i.test(value)) {
    return value;
  }

  const normalizedPath = value.startsWith('/') ? value : `/${value}`;
  return `${BACKEND_BASE_URL}${normalizedPath}`;
};

const getPreferredLanguage = (): 'en' | 'sw' | 'fr' | 'rw' => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  const language = window.localStorage.getItem('haapa.language') || 'en';
  if (language === 'sw' || language === 'fr' || language === 'rw' || language === 'en') {
    return language;
  }

  return 'en';
};

const mapApiUserToShopUser = (apiUser?: {
  id?: string | number;
  _id?: string | number;
  userId?: string | number;
  email?: string;
  role?: string;
  provider?: 'email' | 'google' | 'apple';
  name?: string;
  fullname?: string;
  fullName?: string;
  firstname?: string;
  firstName?: string;
  lastname?: string;
  lastName?: string;
  username?: string;
  preferredLanguage?: 'en' | 'sw' | 'fr' | 'rw';
  phone?: string;
  telephone?: string;
  country?: string;
  dateOfBirth?: string;
  profileImageUrl?: string;
  profileImage?: string;
  profile?: string;
  avatar?: string;
  imageUrl?: string;
  photoUrl?: string;
  picture?: string;
  pictureFile?: string;
  file?: string;
}): User => {
  const safeApiUser = apiUser ?? {};
  const resolvedId = String(safeApiUser.id || safeApiUser._id || safeApiUser.userId || `buyer-${Date.now()}`);
  const rawEmail = safeApiUser.email || safeApiUser.username || `${resolvedId}@haapa.local`;
  const resolvedEmail = normalizeEmail(rawEmail);
  const resolvedName = resolveDisplayName({
    ...safeApiUser,
    email: resolvedEmail,
  });

  return {
    id: resolvedId,
    name: resolvedName,
    email: resolvedEmail,
    role: normalizeRole(safeApiUser.role),
    provider: safeApiUser.provider ?? 'email',
    preferredLanguage: safeApiUser.preferredLanguage ?? getPreferredLanguage(),
    phone: safeApiUser.phone || safeApiUser.telephone,
    country: safeApiUser.country,
    dateOfBirth: safeApiUser.dateOfBirth,
    profileImageUrl: normalizeUserAssetUrl(
      safeApiUser.profileImageUrl ||
      safeApiUser.profileImage ||
      safeApiUser.profile ||
      safeApiUser.avatar ||
      safeApiUser.imageUrl ||
      safeApiUser.photoUrl ||
      safeApiUser.picture ||
      safeApiUser.pictureFile ||
      safeApiUser.file,
    ),
  };
};

const normalizeStoredShopUser = (user: User | null): User | null => {
  if (!user) {
    return null;
  }

  const normalizedEmail = normalizeEmail(user.email);
  const normalizedName = isPlaceholderProfileName(user.name)
    ? buildBuyerNameFromEmail(normalizedEmail)
    : user.name;

  return {
    ...user,
    name: normalizedName,
    email: normalizedEmail,
    profileImageUrl: normalizeUserAssetUrl(user.profileImageUrl),
    preferredLanguage: user.preferredLanguage ?? getPreferredLanguage(),
  };
};

const mergeUserWithFallback = (nextUser: User, fallbackUser?: User | null): User => {
  const normalizedNextUser = normalizeStoredShopUser(nextUser) ?? nextUser;
  const normalizedFallbackUser = normalizeStoredShopUser(fallbackUser ?? null);

  return {
    ...normalizedNextUser,
    name:
      isPlaceholderProfileName(normalizedNextUser.name) && normalizedFallbackUser?.name
        ? normalizedFallbackUser.name
        : normalizedNextUser.name,
    phone: normalizedNextUser.phone ?? normalizedFallbackUser?.phone,
    country: normalizedNextUser.country ?? normalizedFallbackUser?.country,
    dateOfBirth: normalizedNextUser.dateOfBirth ?? normalizedFallbackUser?.dateOfBirth,
    profileImageUrl: normalizedNextUser.profileImageUrl ?? normalizedFallbackUser?.profileImageUrl,
    preferredLanguage: normalizedNextUser.preferredLanguage ?? normalizedFallbackUser?.preferredLanguage ?? getPreferredLanguage(),
  };
};

const getInitialCurrentUser = (): User | null => {
  const storedAuthUser = authService.getStoredUser();
  if (storedAuthUser) {
    return normalizeStoredShopUser(mapApiUserToShopUser(storedAuthUser));
  }

  return normalizeStoredShopUser(readStorage<User | null>(USER_STORAGE_KEY, null));
};

const getInitialThemeMode = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const storedThemeMode = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedThemeMode === 'dark' || storedThemeMode === 'light') {
    return storedThemeMode;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const toCategorySlug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^products\.categories\./, '')
    .replace(/[_\s]+/g, '-');

const toCategoryLabel = (categoryValue: string): string => {
  const normalized = toCategorySlug(categoryValue);
  return normalized
    .split('-')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
};

const deriveCategoriesFromProducts = (catalogProducts: Product[]): Category[] => {
  const dedupedCategories = new Map<string, Category>();

  catalogProducts.forEach((product) => {
    // 1. Try explicit categories array
    // 2. Try parsing categoryKey
    // 3. Try parsing product.category (legacy field sometimes mapped to categoryKey)
    const rawCategory = (product as any).category || product.categoryKey;
    const parsedCategories = product.categories ?? parseCategoryString(String(rawCategory || ''));
    
    const fallbackCategory = rawCategory 
      ? [{ slug: toCategorySlug(String(rawCategory)), label: toCategoryLabel(String(rawCategory)) }] 
      : [];
      
    const categoriesToUse = parsedCategories.length > 0 ? parsedCategories : fallbackCategory;

    categoriesToUse.forEach((category) => {
      if (!category.slug) return;
      const id = category.slug;
      if (dedupedCategories.has(id)) return;
      dedupedCategories.set(id, {
        id,
        name: category.label,
        slug: category.slug,
        audience: product.audience,
      });
    });
  });

  return [...dedupedCategories.values()];
};

const normalizeCatalogAssetUrl = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalizedPath = value.startsWith('/') ? value : `/${value}`;
  return `${BACKEND_BASE_URL}${normalizedPath}`;
};

const readApiListPayload = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.data)) {
    return record.data;
  }

  return [];
};

const mapBookRecordToProduct = (raw: unknown): Product | null => {
  const record = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const uploaderRecord =
    record.uploader && typeof record.uploader === 'object' ? (record.uploader as Record<string, unknown>) : {};
  const creatorRecord =
    record.creator && typeof record.creator === 'object' ? (record.creator as Record<string, unknown>) : {};
  const vendorRecord =
    record.vendor && typeof record.vendor === 'object' ? (record.vendor as Record<string, unknown>) : {};
  const userRecord =
    record.user && typeof record.user === 'object' ? (record.user as Record<string, unknown>) : {};
  const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() ? value.trim() : undefined;
  const asNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && Number.isFinite(Number(value))) return Number(value);
    return undefined;
  };
  const asBool = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const v = value.toLowerCase();
      if (['true', '1', 'yes'].includes(v)) return true;
      if (['false', '0', 'no'].includes(v)) return false;
    }
    return undefined;
  };

  const id = asString(record.id) || asString(record._id) || asString(record.slug);
  if (!id) return null;

  const name = asString(record.name) ?? 'Untitled Book';
  const summary = asString(record.summary) ?? 'No description available.';
  const category = asString(record.category) ?? 'uncategorized';
  const parsedCategories = parseCategoryString(category);
  const primaryCategory = parsedCategories[0];
  const cover = asString(record.coverUrl) ?? undefined;
  const bookUrl = asString(record.bookUrl);
  const audioUrl = asString(record.audioUrl);
  const videoUrl = asString(record.videoUrl);
  const price = asNumber(record.price) ?? 0;
  const mediaType = videoUrl ? 'animation' : audioUrl ? 'audio' : 'book';
  const explicitIsPODAvailable =
    asBool((record as { isPODAvailable?: unknown }).isPODAvailable) ??
    asBool((record as { isPOD?: unknown }).isPOD);
  const explicitIsDigital =
    asBool((record as { isDigital?: unknown }).isDigital) ??
    asBool((record as { digital?: unknown }).digital);
  const isPODAvailable = explicitIsPODAvailable ?? false;
  const isDigital =
    explicitIsDigital ??
    (mediaType === 'audio' || mediaType === 'animation'
      ? true
      : Boolean(bookUrl) && !isPODAvailable);
  const uploaderName =
    asString((record as { uploaderName?: unknown }).uploaderName) ||
    asString((record as { uploadedBy?: unknown }).uploadedBy) ||
    asString((record as { creatorName?: unknown }).creatorName) ||
    asString((record as { vendorName?: unknown }).vendorName) ||
    asString(uploaderRecord.name) ||
    asString(creatorRecord.name) ||
    asString(vendorRecord.name) ||
    asString(userRecord.name) ||
    asString((record as { author?: unknown }).author);
  const uploaderEmail =
    asString((record as { uploaderEmail?: unknown }).uploaderEmail) ||
    asString((record as { creatorEmail?: unknown }).creatorEmail) ||
    asString((record as { vendorEmail?: unknown }).vendorEmail) ||
    asString(uploaderRecord.email) ||
    asString(creatorRecord.email) ||
    asString(vendorRecord.email) ||
    asString(userRecord.email);
  const publisherEmail =
    asString((record as { publisherEmail?: unknown }).publisherEmail) ||
    uploaderEmail;
  const publisherPhone =
    asString((record as { publisherPhone?: unknown }).publisherPhone) ||
    asString((record as { phone?: unknown }).phone) ||
    asString(uploaderRecord.phone) ||
    asString(userRecord.phone);
  const publisherAltPhone =
    asString((record as { publisherAltPhone?: unknown }).publisherAltPhone) ||
    asString((record as { telephone?: unknown }).telephone) ||
    asString(uploaderRecord.telephone) ||
    asString(userRecord.telephone);
  const uploaderId =
    asString((record as { uploaderId?: unknown }).uploaderId) ||
    asString((record as { userId?: unknown }).userId) ||
    asString((record as { creatorId?: unknown }).creatorId) ||
    asString((record as { vendorId?: unknown }).vendorId) ||
    asString(uploaderRecord.id) ||
    asString(creatorRecord.id) ||
    asString(vendorRecord.id) ||
    asString(userRecord.id);

  return {
    id: String(id),
    nameKey: name,
    descriptionKey: summary,
    categoryKey: primaryCategory?.label ?? category,
    categoryId: primaryCategory?.slug ?? toCategorySlug(category),
    categories: parsedCategories.length > 0 ? parsedCategories : undefined,
    audience: 'public',
    mediaType,
    contentUrl: bookUrl || audioUrl || videoUrl || undefined,
    bookUrl: bookUrl || undefined,
    audioUrl: audioUrl || undefined,
    videoUrl: videoUrl || undefined,
    coverUrl: cover,
    hasBookUrl: asBool((record as { hasBookUrl?: unknown }).hasBookUrl) ?? Boolean(bookUrl),
    hasAudioUrl: asBool((record as { hasAudioUrl?: unknown }).hasAudioUrl) ?? Boolean(audioUrl),
    hasVideoUrl: asBool((record as { hasVideoUrl?: unknown }).hasVideoUrl) ?? Boolean(videoUrl),
    isDigital,
    isPODAvailable,
    madeIntoAnimation: asBool((record as { madeIntoAnimation?: unknown }).madeIntoAnimation),
    author: asString((record as { author?: unknown }).author),
    authorName: asString((record as { author?: unknown }).author),
    uploaderId,
    uploaderName,
    uploaderEmail,
    publisherEmail,
    publisherPhone,
    publisherAltPhone,
    slug: asString(record.slug) || String(id),
    publishedCompany: asString((record as { publishedCompany?: unknown }).publishedCompany) || uploaderName,
    price,
    currency: '$',
    imageUrl: cover || '/kids/2cc8c2f37f235aa54af11ab2d12dbaa0.jpg',
    rating: 0,
    reviewCount: 0,
    stock: asNumber((record as { bookCount?: unknown }).bookCount) ?? 0,
    reviews: [],
  };
};

const mapRecordedMediaToProduct = (raw: unknown): Product | null => {
  const record = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const uploaderRecord =
    record.uploader && typeof record.uploader === 'object' ? (record.uploader as Record<string, unknown>) : {};
  const userRecord =
    record.user && typeof record.user === 'object' ? (record.user as Record<string, unknown>) : {};
  const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim()
      ? value.trim()
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : undefined;

  const rawMediaType = asString(record.mediaType)?.toLowerCase();
  if (rawMediaType !== 'audio' && rawMediaType !== 'video' && rawMediaType !== 'animation') {
    return null;
  }

  const id = asString(record.id) || asString(record.recordingId);
  const contentUrl = normalizeCatalogAssetUrl(asString(record.fileUrl) || asString(record.url));
  if (!id || !contentUrl) {
    return null;
  }

  const mediaType = rawMediaType === 'audio' ? 'audio' : 'animation';
  const title =
    asString(record.title) ||
    asString(record.originalName) ||
    (mediaType === 'audio' ? 'Recorded Audio' : 'Recorded Video');
  const summary =
    asString(record.description) ||
    (mediaType === 'audio'
      ? 'Recorded audio captured in the Haapa studio.'
      : 'Recorded video captured in the Haapa studio.');
  const imageUrl =
    normalizeCatalogAssetUrl(asString(record.previewImageUrl) || asString(record.coverUrl) || asString(record.imageUrl)) ||
    '/vite.svg';
  const categoryLabel = mediaType === 'audio' ? 'Recorded Audio' : 'Recorded Video';
  const categorySlug = mediaType === 'audio' ? 'recorded-audio' : 'recorded-video';
  const uploaderName =
    asString(record.uploaderName) ||
    asString(record.uploadedBy) ||
    asString(record.creatorName) ||
    asString(record.vendorName) ||
    asString(uploaderRecord.name) ||
    asString(userRecord.name) ||
    'Haapa Studio';
  const uploaderEmail =
    asString(record.uploaderEmail) ||
    asString(record.creatorEmail) ||
    asString(record.vendorEmail) ||
    asString(uploaderRecord.email) ||
    asString(userRecord.email);
  const publisherEmail =
    asString(record.publisherEmail) ||
    uploaderEmail;
  const publisherPhone =
    asString(record.publisherPhone) ||
    asString(record.phone) ||
    asString(uploaderRecord.phone) ||
    asString(userRecord.phone);
  const publisherAltPhone =
    asString(record.publisherAltPhone) ||
    asString(record.telephone) ||
    asString(uploaderRecord.telephone) ||
    asString(userRecord.telephone);
  const uploaderId =
    asString(record.uploaderId) ||
    asString(record.userId) ||
    asString(record.creatorId) ||
    asString(record.vendorId) ||
    asString(uploaderRecord.id) ||
    asString(userRecord.id);

  return {
    id,
    nameKey: title,
    descriptionKey: summary,
    author: uploaderName,
    authorName: uploaderName,
    uploaderId,
    uploaderName,
    uploaderEmail,
    publisherEmail,
    publisherPhone,
    publisherAltPhone,
    summary,
    categoryKey: categoryLabel,
    categoryId: categorySlug,
    categories: [{ slug: categorySlug, label: categoryLabel }],
    audience: 'public',
    mediaType,
    contentUrl,
    audioUrl: mediaType === 'audio' ? contentUrl : undefined,
    videoUrl: mediaType === 'animation' ? contentUrl : undefined,
    coverUrl: imageUrl,
    hasAudioUrl: mediaType === 'audio',
    hasVideoUrl: mediaType === 'animation',
    isDigital: true,
    isPODAvailable: false,
    price: 0,
    currency: '$',
    imageUrl,
    rating: 0,
    reviewCount: 0,
    stock: 1,
    reviews: [],
  };
};

export const ShopProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(() => getInitialCurrentUser());
  const [users, setUsers] = useState<User[]>(() => (currentUser ? [currentUser] : []));
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>(() => readStorage<Order[]>(ORDER_STORAGE_KEY, []));
  const [wishlist, setWishlist] = useState<string[]>(() => readStorage<string[]>(WISHLIST_STORAGE_KEY, []));
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => getInitialThemeMode());
  const [buyerProfiles, setBuyerProfiles] = useState<Record<string, User>>(() =>
    readStorage<Record<string, User>>(BUYER_PROFILES_STORAGE_KEY, {}),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);

  const cartSubtotal = useMemo(
    () => cart.reduce((sum, cartItem) => sum + cartItem.product.price * cartItem.quantity, 0),
    [cart],
  );

  const cartTotalItems = useMemo(
    () => cart.reduce((sum, cartItem) => sum + cartItem.quantity, 0),
    [cart],
  );

  useEffect(() => {
    window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(currentUser));
  }, [currentUser]);

  useEffect(() => {
    window.localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(orders));
  }, [orders]);

  useEffect(() => {
    window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    window.localStorage.setItem(BUYER_PROFILES_STORAGE_KEY, JSON.stringify(buyerProfiles));
  }, [buyerProfiles]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'buyer' || currentUser.provider !== 'email') {
      return;
    }

    const normalizedEmail = normalizeEmail(currentUser.email);
    setBuyerProfiles((previousProfiles) => {
      const existingProfile = previousProfiles[normalizedEmail];
      if (
        existingProfile &&
        existingProfile.dateOfBirth === currentUser.dateOfBirth &&
        existingProfile.name === currentUser.name &&
        existingProfile.phone === currentUser.phone &&
        existingProfile.country === currentUser.country &&
        existingProfile.profileImageUrl === currentUser.profileImageUrl
      ) {
        return previousProfiles;
      }

      return {
        ...previousProfiles,
        [normalizedEmail]: {
          ...currentUser,
          email: normalizedEmail,
        },
      };
    });
  }, [currentUser]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  const refreshCatalog = async () => {
    setIsCatalogLoading(true);
    try {
      const fetchPayload = async (paths: string[]): Promise<unknown> => {
        let lastError: Error | null = null;

        for (let index = 0; index < paths.length; index += 1) {
          const path = paths[index];

          try {
            const response = await fetch(`${API_BASE_URL}${path}`, {
              headers: {
                Accept: '*/*',
              },
            });

            if (!response.ok) {
              if ([401, 403, 404, 405].includes(response.status) && index < paths.length - 1) {
                continue;
              }

              throw new Error(`Request failed with status ${response.status}`);
            }

            return await response.json().catch(() => []);
          } catch (error: unknown) {
            lastError = error instanceof Error ? error : new Error('Request failed');
          }
        }

        throw lastError ?? new Error('Request failed');
      };

      const bookPaths =
        currentUser?.role === 'vendor'
          ? ['/books/me/mine', '/books', '/books/approved']
          : currentUser?.role === 'admin' || currentUser?.role === 'manager'
            ? ['/books', '/books/approved']
            : ['/books', '/books/approved'];

      const [booksPayload, audioPayload, animationPayload] = await Promise.allSettled([
        fetchPayload(bookPaths),
        fetchPayload(['/audio']),
        fetchPayload(['/books/made-into-animation']),
      ]);

      const normalizedBooks =
        (booksPayload.status === 'fulfilled' ? readApiListPayload(booksPayload.value) : [])
          .map((entry) => mapBookRecordToProduct(entry))
          .filter((item): item is Product => Boolean(item));

      const normalizedAudio =
        (audioPayload.status === 'fulfilled' ? readApiListPayload(audioPayload.value) : [])
          .map((entry) => mapBookRecordToProduct(entry))
          .filter((item): item is Product => Boolean(item));

      const normalizedAnimations =
        (animationPayload.status === 'fulfilled' ? readApiListPayload(animationPayload.value) : [])
          .map((entry) => mapBookRecordToProduct(entry))
          .filter((item): item is Product => Boolean(item));

      const mergedCatalog = [...normalizedBooks, ...normalizedAudio, ...normalizedAnimations].filter(
        (item, index, items) => items.findIndex((candidate) => candidate.id === item.id) === index,
      );

      if (mergedCatalog.length > 0) {
        setProducts(mergedCatalog);
        setCategories(deriveCategoriesFromProducts(mergedCatalog));
        return;
      }

      setProducts(productCatalog);
      setCategories(deriveCategoriesFromProducts(productCatalog));
    } catch (error) {
      console.warn('Catalog refresh failed, using static fallback:', error);
      setProducts(productCatalog);
      setCategories(deriveCategoriesFromProducts(productCatalog));
    } finally {
      setIsCatalogLoading(false);
    }
  };

  useEffect(() => {
    void refreshCatalog();
  }, []);

  const normalizeProductFromApi = (rawProduct: unknown, productIdHint?: string): Product | null => {
    const record = rawProduct && typeof rawProduct === 'object' ? (rawProduct as Record<string, unknown>) : {};
    const uploaderRecord =
      record.uploader && typeof record.uploader === 'object' ? (record.uploader as Record<string, unknown>) : {};
    const creatorRecord =
      record.creator && typeof record.creator === 'object' ? (record.creator as Record<string, unknown>) : {};
    const vendorRecord =
      record.vendor && typeof record.vendor === 'object' ? (record.vendor as Record<string, unknown>) : {};
    const userRecord =
      record.user && typeof record.user === 'object' ? (record.user as Record<string, unknown>) : {};

    const resolvedId =
      (typeof record.id === 'string' && record.id.trim()) ||
      (typeof record._id === 'string' && record._id.trim()) ||
      (typeof record.productId === 'string' && record.productId.trim()) ||
      (typeof record.product_id === 'string' && record.product_id.trim()) ||
      productIdHint;

    if (!resolvedId) {
      return null;
    }

    const lookup = new Map<string, Product>([...productCatalog, ...products].map((item) => [item.id, item]));

    const asString = (value: unknown): string | undefined =>
      typeof value === 'string' && value.trim()
        ? value.trim()
        : typeof value === 'number' && Number.isFinite(value)
          ? String(value)
          : undefined;

    const asNumber = (value: unknown, fallbackValue?: number): number => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && Number.isFinite(Number(value))) {
        return Number(value);
      }
      return fallbackValue ?? 0;
    };

    const fallbackProduct = lookup.get(resolvedId);
    const resolvedAudioUrl = asString(record.audioUrl) || asString(record.audio_url) || fallbackProduct?.audioUrl;
    const resolvedVideoUrl = asString(record.videoUrl) || asString(record.video_url) || fallbackProduct?.videoUrl;
    const resolvedBookUrl = asString(record.bookUrl) || fallbackProduct?.bookUrl;
    const resolvedCoverUrl = asString(record.coverUrl) || asString(record.imageUrl) || fallbackProduct?.coverUrl;
    const uploaderName =
      asString((record as Record<string, unknown>).uploaderName) ||
      asString((record as Record<string, unknown>).uploadedBy) ||
      asString((record as Record<string, unknown>).creatorName) ||
      asString((record as Record<string, unknown>).vendorName) ||
      asString((record as Record<string, unknown>).publisherName) ||
      asString(uploaderRecord.name) ||
      asString(creatorRecord.name) ||
      asString(vendorRecord.name) ||
      asString(userRecord.name) ||
      fallbackProduct?.uploaderName ||
      fallbackProduct?.authorName ||
      fallbackProduct?.author;
    const uploaderEmail =
      asString((record as Record<string, unknown>).uploaderEmail) ||
      asString((record as Record<string, unknown>).creatorEmail) ||
      asString((record as Record<string, unknown>).vendorEmail) ||
      asString((record as Record<string, unknown>).publisherEmail) ||
      asString(uploaderRecord.email) ||
      asString(creatorRecord.email) ||
      asString(vendorRecord.email) ||
      asString(userRecord.email) ||
      fallbackProduct?.uploaderEmail;
    const publisherEmail =
      asString((record as Record<string, unknown>).publisherEmail) ||
      fallbackProduct?.publisherEmail ||
      uploaderEmail;
    const publisherPhone =
      asString((record as Record<string, unknown>).publisherPhone) ||
      asString((record as Record<string, unknown>).phone) ||
      fallbackProduct?.publisherPhone;
    const publisherAltPhone =
      asString((record as Record<string, unknown>).publisherAltPhone) ||
      asString((record as Record<string, unknown>).telephone) ||
      fallbackProduct?.publisherAltPhone;
    const uploaderId =
      asString((record as Record<string, unknown>).uploaderId) ||
      asString((record as Record<string, unknown>).userId) ||
      asString((record as Record<string, unknown>).creatorId) ||
      asString((record as Record<string, unknown>).vendorId) ||
      asString((record as Record<string, unknown>).ownerId) ||
      asString(uploaderRecord.id) ||
      asString(creatorRecord.id) ||
      asString(vendorRecord.id) ||
      asString(userRecord.id) ||
      fallbackProduct?.uploaderId;

    return {
      id: resolvedId,
      nameKey:
        asString(record.nameKey) ||
        asString(record.title) ||
        asString(record.name) ||
        asString((record as Record<string, unknown>).productName) ||
        fallbackProduct?.nameKey ||
        'products.name',
      descriptionKey:
        asString(record.descriptionKey) ||
        asString(record.description) ||
        fallbackProduct?.descriptionKey ||
        'products.description',
      categoryKey:
        asString(record.categoryKey) ||
        asString(record.category) ||
        asString((record as Record<string, unknown>).categoryName) ||
        fallbackProduct?.categoryKey ||
        'products.categories.children',
      categoryId: asString(record.categoryId) || asString(record.category_id) || fallbackProduct?.categoryId,
      audience:
        record.audience === 'kids' || record.audience === 'adults' || record.audience === 'public'
          ? record.audience
          : fallbackProduct?.audience,
      mediaType:
        normalizeProductMediaType(record.mediaType) ||
        normalizeProductMediaType(record.contentType) ||
        normalizeProductMediaType(record.format) ||
        normalizeProductMediaType(record.type) ||
        fallbackProduct?.mediaType ||
        'book',
      contentUrl:
        asString(record.contentUrl) ||
        asString(record.fileUrl) ||
        asString(record.downloadUrl) ||
        asString(record.streamUrl) ||
        resolvedBookUrl ||
        resolvedAudioUrl ||
        resolvedVideoUrl ||
        fallbackProduct?.contentUrl,
      audioUrl: resolvedAudioUrl,
      videoUrl: resolvedVideoUrl,
      bookUrl: resolvedBookUrl,
      coverUrl: resolvedCoverUrl,
      hasBookUrl: typeof record.hasBookUrl === 'boolean' ? record.hasBookUrl : Boolean(resolvedBookUrl),
      hasAudioUrl: typeof record.hasAudioUrl === 'boolean' ? record.hasAudioUrl : Boolean(resolvedAudioUrl),
      hasVideoUrl: typeof record.hasVideoUrl === 'boolean' ? record.hasVideoUrl : Boolean(resolvedVideoUrl),
      isDigital:
        typeof record.isDigital === 'boolean'
          ? record.isDigital
          : typeof (record as Record<string, unknown>).digital === 'boolean'
            ? Boolean((record as Record<string, unknown>).digital)
            : fallbackProduct?.isDigital,
      isPODAvailable:
        typeof record.isPODAvailable === 'boolean'
          ? record.isPODAvailable
          : typeof (record as Record<string, unknown>).isPOD === 'boolean'
            ? Boolean((record as Record<string, unknown>).isPOD)
            : fallbackProduct?.isPODAvailable,
      madeIntoAnimation: typeof record.madeIntoAnimation === 'boolean' ? record.madeIntoAnimation : fallbackProduct?.madeIntoAnimation,
      author:
        asString(record.author) ||
        asString((record as Record<string, unknown>).authorName) ||
        asString((record as Record<string, unknown>).creatorName) ||
        asString((record as Record<string, unknown>).vendorName) ||
        asString((record as Record<string, unknown>).publisherName) ||
        asString((record as Record<string, unknown>).uploadedBy) ||
        asString((record as Record<string, unknown>).uploaderName) ||
        fallbackProduct?.author ||
        fallbackProduct?.authorName,
      authorName:
        asString((record as Record<string, unknown>).authorName) ||
        asString(record.author) ||
        asString((record as Record<string, unknown>).creatorName) ||
        asString((record as Record<string, unknown>).vendorName) ||
        asString((record as Record<string, unknown>).publisherName) ||
        asString((record as Record<string, unknown>).uploadedBy) ||
        asString((record as Record<string, unknown>).uploaderName) ||
        fallbackProduct?.authorName ||
        fallbackProduct?.author,
      uploaderId,
      uploaderName,
      uploaderEmail,
      publisherEmail,
      publisherPhone,
      publisherAltPhone,
      summary: asString(record.summary) || fallbackProduct?.summary,
      slug: asString(record.slug) || fallbackProduct?.slug,
      publishedCompany: asString(record.publishedCompany) || uploaderName || fallbackProduct?.publishedCompany,
      price: asNumber(record.price, fallbackProduct?.price),
      imageUrl:
        asString(record.coverUrl) ||
        asString(record.imageUrl) ||
        fallbackProduct?.coverUrl ||
        fallbackProduct?.imageUrl ||
        '/placeholder-book.png',
      rating: asNumber(record.rating, fallbackProduct?.rating ?? 4.5),
      reviewCount: Math.max(0, Math.floor(asNumber(record.reviewCount, fallbackProduct?.reviewCount ?? 0))),
      stock: Math.max(0, Math.floor(asNumber(record.stock, fallbackProduct?.stock ?? 0))),
      reviews: Array.isArray(record.reviews) ? (record.reviews as Product['reviews']) : fallbackProduct?.reviews ?? [],
    };
  };

  const mapCartItemsFromApi = (apiItems: CartApiItem[]): CartItem[] => {
    if (!apiItems || apiItems.length === 0) {
      return [];
    }

    const normalized: CartItem[] = [];

    apiItems.forEach((item) => {
      const itemRecord = item as Record<string, unknown>;
      const bookId =
        typeof itemRecord.bookId === 'string'
          ? itemRecord.bookId
          : typeof itemRecord.bookId === 'number' && Number.isFinite(itemRecord.bookId)
            ? String(itemRecord.bookId)
            : undefined;
      const productIdHint = item.productId || bookId;
      const product = normalizeProductFromApi(item.product ?? item, productIdHint);
      const quantity = Math.max(1, Math.floor(Number(item.quantity ?? 1)));
      if (product && quantity > 0) {
        const explicitIsPOD = (item as unknown as { isPOD?: unknown }).isPOD;
        const explicitIsDigital = (item as unknown as { isDigital?: unknown }).isDigital;
        const hasDigitalMedia =
          Boolean(product.audioUrl) ||
          Boolean(product.videoUrl) ||
          Boolean(product.bookUrl) ||
          Boolean(product.contentUrl);
        const isPOD =
          typeof explicitIsPOD === 'boolean'
            ? explicitIsPOD
            : product.isPODAvailable === true;
        const isDigital =
          typeof explicitIsDigital === 'boolean'
            ? explicitIsDigital
            : product.isDigital === true ||
              (((product.mediaType === 'audio' || product.mediaType === 'animation' || hasDigitalMedia) &&
                isPOD !== true));
        normalized.push({
          product,
          quantity,
          ...(isPOD ? { isPOD } : {}),
          ...(isDigital ? { isDigital } : {}),
        });
      }
    });

    return normalized;
  };

  const normalizeOrderFromApi = (rawOrder: unknown): Order | null => {
    const record = rawOrder && typeof rawOrder === 'object' ? (rawOrder as Record<string, unknown>) : {};
    const dataRecord = record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : {};

    // Handle /payments/my format (Library/Ownership)
    const paidBookInfo = record.paidBookInfo || dataRecord.paidBookInfo;
    if (paidBookInfo) {
      const bookRecord = paidBookInfo as Record<string, unknown>;
      const bookId = String(bookRecord.id || '');
      const product = normalizeProductFromApi(bookRecord, bookId);
      const amount = Number(record.amount || dataRecord.amount || 0);
      const status = String(record.status || dataRecord.status || 'succeeded');

      return {
        id: String(record.id || dataRecord.id || `own-${bookId}`),
        buyerName: currentUser?.name || 'Me',
        buyerEmail: currentUser?.email || '',
        buyerId: currentUser?.id || '',
        items: [{ product, quantity: 1, isDigital: true, isPOD: false }],
        subtotal: amount,
        shippingFee: 0,
        total: amount,
        paymentMethod: 'card',
        currency: String(record.currency || dataRecord.currency || 'USD'),
        paymentStatus: status.toLowerCase() === 'succeeded' || status.toLowerCase() === 'paid' ? 'paid' : 'pending',
        status: status.toLowerCase() === 'succeeded' || status.toLowerCase() === 'paid' ? 'Paid' : 'Pending',
        createdAt: String(record.createdAt || dataRecord.createdAt || new Date().toISOString()),
        shippingAddress: { fullName: '', phone: '', street: '', city: '', country: '' },
      };
    }

    const asRecord = (value: unknown): Record<string, unknown> =>
      value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

    const asString = (value: unknown): string | undefined =>
      typeof value === 'string' && value.trim()
        ? value.trim()
        : typeof value === 'number' && Number.isFinite(value)
          ? String(value)
          : undefined;

    const normalizePaymentMethod = (value: unknown): Order['paymentMethod'] => {
      const normalizedValue = asString(value)?.toLowerCase();
      if (!normalizedValue) {
        return 'card';
      }

      if (normalizedValue === 'card' || normalizedValue === 'cards' || normalizedValue === 'flutterwave') {
        return 'card';
      }
      if (
        normalizedValue === 'cod' ||
        normalizedValue === 'cashondelivery' ||
        normalizedValue === 'cash_on_delivery' ||
        normalizedValue === 'cash-on-delivery'
      ) {
        return 'cod';
      }
      if (normalizedValue === 'paypal') {
        return 'paypal';
      }
      if (
        normalizedValue === 'mobilemoney' ||
        normalizedValue === 'mobile_money' ||
        normalizedValue === 'mobile-money' ||
        normalizedValue === 'momo'
      ) {
        return 'mobileMoney';
      }

      return 'card';
    };

    const resolvedId =
      (typeof record.id === 'string' && record.id.trim()) ||
      (typeof record._id === 'string' && record._id.trim()) ||
      (typeof record.orderId === 'string' && record.orderId.trim()) ||
      (typeof dataRecord.id === 'string' && dataRecord.id.trim()) ||
      (typeof dataRecord._id === 'string' && dataRecord._id.trim()) ||
      (typeof dataRecord.orderId === 'string' && dataRecord.orderId.trim());

    if (!resolvedId) {
      return null;
    }

    const items =
      Array.isArray(record.items) && record.items.length > 0
        ? record.items
        : Array.isArray(dataRecord.items)
          ? dataRecord.items
          : [];
    const normalizedItems = mapCartItemsFromApi(items as CartApiItem[]);

    const normalizedPaymentStatus = (() => {
      const rawPaymentStatus =
        asString(record.paymentStatus) ||
        asString(dataRecord.paymentStatus) ||
        asString(asRecord(record.payment).status) ||
        asString(asRecord(dataRecord.payment).status) ||
        '';
      const value = rawPaymentStatus.trim().toLowerCase();
      if (['paid', 'successful', 'succeeded', 'success', 'completed'].includes(value)) {
        return 'paid';
      }
      if (['failed', 'failure', 'cancelled', 'canceled'].includes(value)) {
        return 'failed';
      }
      return 'pending';
    })();

    const rawFulfillmentType = (
      asString(record.fulfillmentType) ||
      asString(dataRecord.fulfillmentType)
    )?.toLowerCase();
    const explicitFulfillmentType =
      rawFulfillmentType === 'digital' || rawFulfillmentType === 'delivery'
        ? rawFulfillmentType
        : undefined;

    const rawStatusHistoryEntries = Array.isArray(record.statusHistory) && record.statusHistory.length > 0
      ? (record.statusHistory as unknown[])
      : Array.isArray(dataRecord.statusHistory) && dataRecord.statusHistory.length > 0
        ? (dataRecord.statusHistory as unknown[])
        : [];
    const hasDeliveryWorkflowHistory = rawStatusHistoryEntries.some((entry) => {
      const entryStatus = asString(asRecord(entry).status)?.trim().toLowerCase();
      return Boolean(
        entryStatus &&
          ['processed', 'processing', 'shipped', 'en route', 'en-route', 'en_route', 'arrived', 'delivered'].includes(
            entryStatus,
          ),
      );
    });

    const isDigitalOnlyOrder =
      explicitFulfillmentType === 'digital' ||
      (explicitFulfillmentType !== 'delivery' &&
        normalizedItems.length > 0 &&
        !hasDeliveryWorkflowHistory &&
        normalizedItems.every((item) => {
          const mediaType = item.product.mediaType ?? 'book';
          const hasDigitalMedia =
            Boolean(item.product.audioUrl) ||
            Boolean(item.product.videoUrl) ||
            Boolean(item.product.bookUrl) ||
            Boolean(item.product.contentUrl);
          if (item.isPOD || item.product.isPODAvailable === true) {
            return false;
          }
          if (mediaType === 'audio' || mediaType === 'animation') {
            return true;
          }
          return item.isDigital === true || item.product.isDigital === true || hasDigitalMedia;
        }));

    const rawStatus = (record.status || dataRecord.status) as string | undefined;
    const statusValue = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : undefined;
    const statusMapping: Record<string, OrderStatus> = {
      pending: 'Processed',
      processed: 'Processed',
      processing: 'Processed',
      paid: 'Processed',
      unpaid: 'Processed',
      not_paid: 'Processed',
      'not paid': 'Processed',
      design_review: 'Processed',
      designreview: 'Processed',
      printing: 'Processed',
      quality_check: 'Processed',
      qualitycheck: 'Processed',
      shipped: 'Shipped',
      en_route: 'En Route',
      enroute: 'En Route',
      'en-route': 'En Route',
      'en route': 'En Route',
      arrived: 'Arrived',
      delivered: 'Arrived',
      cancelled: 'Cancelled',
      canceled: 'Cancelled',
      failed: 'Failed',
    };
    const normalizedStatus: OrderStatus =
      statusValue && statusMapping[statusValue]
        ? statusMapping[statusValue]
        : statusValue?.includes('ship')
          ? 'Shipped'
          : statusValue?.includes('route')
            ? 'En Route'
            : statusValue?.includes('arriv') || statusValue?.includes('deliver')
              ? 'Arrived'
              : 'Processed';

    const normalizeHistoryStatus = (value: unknown): OrderStatus => {
      const key = typeof value === 'string' ? value.trim().toLowerCase() : '';
      return statusMapping[key] ?? 'Processed';
    };

    const derivedDigitalStatus: OrderStatus =
      normalizedPaymentStatus === 'paid'
        ? 'Paid'
        : normalizedPaymentStatus === 'failed'
          ? 'Failed'
          : 'Pending';

    const asNumber = (value: unknown, fallbackValue = 0): number => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && Number.isFinite(Number(value))) return Number(value);
      return fallbackValue;
    };

    const timestamp = (typeof record.createdAt === 'string' && record.createdAt) || (typeof dataRecord.createdAt === 'string' && dataRecord.createdAt);
    const buyerRecord = asRecord(record.buyer);
    const dataBuyerRecord = asRecord(dataRecord.buyer);
    const userRecord = asRecord(record.user);
    const dataUserRecord = asRecord(dataRecord.user);
    const userIdRecord = asRecord(record.userId);
    const dataUserIdRecord = asRecord(dataRecord.userId);
    const customerRecord = asRecord(record.customer);
    const dataCustomerRecord = asRecord(dataRecord.customer);

    const buyerId =
      asString(record.buyerId) ||
      asString(record.userId) ||
      asString(dataRecord.buyerId) ||
      asString(dataRecord.userId) ||
      asString(record.user_id) ||
      asString(dataRecord.user_id) ||
      asString(buyerRecord.id) ||
      asString(buyerRecord._id) ||
      asString(buyerRecord.userId) ||
      asString(dataBuyerRecord.id) ||
      asString(dataBuyerRecord._id) ||
      asString(dataBuyerRecord.userId) ||
      asString(userRecord.id) ||
      asString(userRecord._id) ||
      asString(userRecord.userId) ||
      asString(dataUserRecord.id) ||
      asString(dataUserRecord._id) ||
      asString(dataUserRecord.userId) ||
      asString(userIdRecord.id) ||
      asString(userIdRecord._id) ||
      asString(dataUserIdRecord.id) ||
      asString(dataUserIdRecord._id) ||
      asString(customerRecord.id) ||
      asString(customerRecord._id) ||
      asString(customerRecord.userId) ||
      asString(dataCustomerRecord.id) ||
      asString(dataCustomerRecord._id) ||
      asString(dataCustomerRecord.userId);

    const buyerName =
      asString(record.buyerName) ||
      asString(record.fullName) ||
      asString(dataRecord.buyerName) ||
      asString(dataRecord.fullName) ||
      asString(buyerRecord.name) ||
      asString(buyerRecord.fullName) ||
      asString(dataBuyerRecord.name) ||
      asString(dataBuyerRecord.fullName) ||
      asString(userRecord.name) ||
      asString(userRecord.fullName) ||
      asString(dataUserRecord.name) ||
      asString(dataUserRecord.fullName) ||
      asString(userIdRecord.fullname) ||
      asString(userIdRecord.fullName) ||
      asString(userIdRecord.username) ||
      asString(dataUserIdRecord.fullname) ||
      asString(dataUserIdRecord.fullName) ||
      asString(dataUserIdRecord.username) ||
      asString(customerRecord.name) ||
      asString(customerRecord.fullName) ||
      asString(dataCustomerRecord.name) ||
      asString(dataCustomerRecord.fullName) ||
      'Buyer';

    const buyerEmail =
      asString(record.buyerEmail) ||
      asString(record.email) ||
      asString(dataRecord.buyerEmail) ||
      asString(dataRecord.email) ||
      asString(buyerRecord.email) ||
      asString(dataBuyerRecord.email) ||
      asString(userRecord.email) ||
      asString(dataUserRecord.email) ||
      asString(userIdRecord.email) ||
      asString(dataUserIdRecord.email) ||
      asString(customerRecord.email) ||
      asString(dataCustomerRecord.email) ||
      `${resolvedId}@haapa.local`;

    const shippingAddressRaw =
      (record.shippingAddress && typeof record.shippingAddress === 'object'
        ? (record.shippingAddress as Record<string, unknown>)
        : dataRecord.shippingAddress && typeof dataRecord.shippingAddress === 'object'
          ? (dataRecord.shippingAddress as Record<string, unknown>)
          : undefined);

    const shippingAddress = shippingAddressRaw
      ? {
          fullName: typeof shippingAddressRaw.fullName === 'string' ? shippingAddressRaw.fullName : buyerName,
          phone: typeof shippingAddressRaw.phone === 'string' ? shippingAddressRaw.phone : '',
          street:
            typeof shippingAddressRaw.addressLine1 === 'string'
              ? shippingAddressRaw.addressLine1
              : typeof shippingAddressRaw.street === 'string'
                ? shippingAddressRaw.street
                : '',
          city: typeof shippingAddressRaw.city === 'string' ? shippingAddressRaw.city : '',
          country: typeof shippingAddressRaw.country === 'string' ? shippingAddressRaw.country : '',
          postalCode: typeof shippingAddressRaw.postalCode === 'string' ? shippingAddressRaw.postalCode : '',
        }
      : {
          fullName: buyerName,
          phone: '',
          street: '',
          city: '',
          country: '',
          postalCode: '',
        };

    const paymentRecord = asRecord(record.payment);
    const dataPaymentRecord = asRecord(dataRecord.payment);
    const podRecord = asRecord(record.pod);
    const dataPodRecord = asRecord(dataRecord.pod);

    const paymentMethod = normalizePaymentMethod(
      record.paymentMethod ||
      record.method ||
      dataRecord.paymentMethod ||
      dataRecord.method ||
      paymentRecord.method ||
      dataPaymentRecord.method ||
      paymentRecord.paymentMethod ||
      dataPaymentRecord.paymentMethod,
    );

    return {
      id: resolvedId,
      orderName: asString(record.orderName) || asString(dataRecord.orderName),
      buyerName,
      buyerEmail,
      buyerId,
      items: normalizedItems,
      subtotal: asNumber(record.subtotal ?? dataRecord.subtotal, 0),
      shippingFee: asNumber(record.shippingFee ?? dataRecord.shippingFee, 0),
      total: asNumber(record.total ?? dataRecord.total, 0),
      paymentMethod,
      currency:
        typeof record.currency === 'string'
          ? record.currency
          : typeof dataRecord.currency === 'string'
            ? dataRecord.currency
            : undefined,
      paymentStatus:
        asString(record.paymentStatus) ||
        asString(dataRecord.paymentStatus) ||
        asString(paymentRecord.status) ||
        asString(dataPaymentRecord.status),
      paymentReference:
        asString(record.paymentReference) ||
        asString(dataRecord.paymentReference) ||
        asString(record.reference) ||
        asString(dataRecord.reference) ||
        asString(paymentRecord.reference) ||
        asString(dataPaymentRecord.reference),
      paymentUrl:
        asString(record.paymentUrl) ||
        asString(record.paymentLink) ||
        asString(dataRecord.paymentUrl) ||
        asString(dataRecord.paymentLink) ||
        asString(record.redirectUrl) ||
        asString(dataRecord.redirectUrl) ||
        asString(record.link) ||
        asString(dataRecord.link) ||
        asString(paymentRecord.paymentUrl) ||
        asString(dataPaymentRecord.paymentUrl) ||
        asString(paymentRecord.link) ||
        asString(dataPaymentRecord.link),
      paymentIntentId:
        asString(record.paymentIntentId) ||
        asString(dataRecord.paymentIntentId) ||
        asString(record.paymentId) ||
        asString(dataRecord.paymentId) ||
        asString(paymentRecord.paymentIntentId) ||
        asString(dataPaymentRecord.paymentIntentId) ||
        asString(paymentRecord.id) ||
        asString(dataPaymentRecord.id),
      paymentClientSecret:
        asString(record.paymentClientSecret) ||
        asString(dataRecord.paymentClientSecret) ||
        asString(record.clientSecret) ||
        asString(dataRecord.clientSecret) ||
        asString(paymentRecord.clientSecret) ||
        asString(dataPaymentRecord.clientSecret) ||
        asString(paymentRecord.client_secret) ||
        asString(dataPaymentRecord.client_secret),
      podProjectId:
        asString(record.podProjectId) ||
        asString(dataRecord.podProjectId) ||
        asString(podRecord.projectId) ||
        asString(dataPodRecord.projectId),
      podOrderId:
        asString(record.podOrderId) ||
        asString(dataRecord.podOrderId) ||
        asString(podRecord.orderId) ||
        asString(dataPodRecord.orderId),
      fulfillmentType: explicitFulfillmentType ?? (isDigitalOnlyOrder ? 'digital' : 'delivery'),
      shippingAddress,
      status: isDigitalOnlyOrder ? derivedDigitalStatus : normalizedStatus,
      statusHistory:
        isDigitalOnlyOrder
          ? [
              { status: 'Pending', timestamp: timestamp || new Date().toISOString() },
              ...(derivedDigitalStatus === 'Paid'
                ? [{ status: 'Paid' as OrderStatus, timestamp: timestamp || new Date().toISOString() }]
                : derivedDigitalStatus === 'Failed'
                  ? [{ status: 'Failed' as OrderStatus, timestamp: timestamp || new Date().toISOString() }]
                  : []),
            ]
          : Array.isArray(record.statusHistory) && record.statusHistory.length > 0
            ? (record.statusHistory as unknown[]).map((entry) => {
                const entryRecord = asRecord(entry);
                return {
                  status: normalizeHistoryStatus(entryRecord.status),
                  timestamp:
                    asString(entryRecord.timestamp) ||
                    asString(entryRecord.createdAt) ||
                    timestamp ||
                    new Date().toISOString(),
                };
              })
            : Array.isArray(dataRecord.statusHistory) && dataRecord.statusHistory.length > 0
              ? (dataRecord.statusHistory as unknown[]).map((entry) => {
                  const entryRecord = asRecord(entry);
                  return {
                    status: normalizeHistoryStatus(entryRecord.status),
                    timestamp:
                      asString(entryRecord.timestamp) ||
                      asString(entryRecord.createdAt) ||
                      timestamp ||
                      new Date().toISOString(),
                  };
                })
              : [{ status: normalizedStatus, timestamp: timestamp || new Date().toISOString() }],
      trackingNumber: asString(record.trackingNumber) || asString(dataRecord.trackingNumber),
      cancellationReason: asString(record.cancellationReason) || asString(dataRecord.cancellationReason),
      createdAt: timestamp || new Date().toISOString(),
    };
  };

  const mapOrdersFromApi = (payload: unknown): Order[] => {
    const asRecord = (value: unknown): Record<string, unknown> =>
      value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

    if (Array.isArray(payload)) {
      return payload.map((entry) => normalizeOrderFromApi(entry)).filter((entry): entry is Order => Boolean(entry));
    }

    const record = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
    const dataRecord = record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : {};
    if (Array.isArray(record.data)) {
      return record.data.map((entry) => normalizeOrderFromApi(entry)).filter((entry): entry is Order => Boolean(entry));
    }

    const candidates = [record.orders, record.items, dataRecord.orders, dataRecord.items];

    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        return candidate
          .map((entry) => normalizeOrderFromApi(entry))
          .filter((entry): entry is Order => Boolean(entry));
      }
    }

    const singleOrderCandidates = [
      record.order,
      dataRecord.order,
      asRecord(record.data).order,
      asRecord(dataRecord.data).order,
    ];

    for (const candidate of singleOrderCandidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      const mergedCandidate = {
        ...(candidate as Record<string, unknown>),
        payment: record.payment || dataRecord.payment,
      };
      const singleOrder = normalizeOrderFromApi(mergedCandidate);
      if (singleOrder) {
        return [singleOrder];
      }
    }

    const single = normalizeOrderFromApi(payload);
    return single ? [single] : [];
  };

  const syncCartFromApi = async () => {
    if (!currentUser) {
      setCart([]);
      return;
    }

    try {
      const apiItems = await cartService.getCart();
      const normalized = mapCartItemsFromApi(apiItems);
      setCart(normalized);
    } catch {
      // Silence cart sync errors to prevent console noise; use local state if backend is unstable.
    }
  };

  useEffect(() => {
    if (currentUser) {
      const storedCart = readStorage<CartItem[]>(getCartStorageKey(currentUser), []);
      if (storedCart.length > 0) {
        setCart(storedCart);
      }

      void syncCartFromApi();
      return;
    }

    if (typeof window !== 'undefined') {
      try {
        Object.keys(window.localStorage).forEach((key) => {
          if (key.startsWith(CART_STORAGE_KEY_PREFIX)) {
            window.localStorage.removeItem(key);
          }
        });
      } catch {
        // ignore storage errors
      }
    }

    setCart([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(getCartStorageKey(currentUser), JSON.stringify(cart));
  }, [cart, currentUser]);

  const refreshCart = async () => {
    await syncCartFromApi();
  };

  const refreshUsers = async () => {
    setUsers(currentUser ? [currentUser] : []);
  };

  const refreshOrders = async () => {
    if (!currentUser) {
      setOrders([]);
      return;
    }

    if (!authService.getToken()) {
      setOrders([]);
      return;
    }

    try {
      const payload =
        currentUser.role === 'admin' || currentUser.role === 'manager'
          ? await orderService.getAdminOrders()
          : await orderService.getOrders();
      const normalizedOrders = mapOrdersFromApi(payload);
      if (currentUser.role !== 'admin' && currentUser.role !== 'manager') {
        const normalizedCurrentEmail = normalizeEmail(currentUser.email);
        const userOnlyOrders = normalizedOrders.filter((order) => {
          const normalizedOrderEmail =
            typeof order.buyerEmail === 'string' && order.buyerEmail.trim()
              ? normalizeEmail(order.buyerEmail)
              : '';
          const normalizedOrderBuyerId =
            typeof order.buyerId === 'string' ? order.buyerId.trim() : '';
          const normalizedCurrentBuyerId = currentUser.id.trim();

          return (
            (normalizedOrderEmail !== '' && normalizedOrderEmail === normalizedCurrentEmail) ||
            (normalizedOrderBuyerId !== '' && normalizedOrderBuyerId === normalizedCurrentBuyerId)
          );
        });
        setOrders(userOnlyOrders);
        return;
      }

      setOrders(normalizedOrders);
    } catch (error) {
      console.error('Failed to load orders from API', error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      void refreshOrders();
    } else {
      setOrders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setUsers([]);
      return;
    }

    if (currentUser.role === 'admin') {
      void refreshUsers();
      return;
    }

    setUsers([currentUser]);
  }, [currentUser]);

  const createProduct = async (): Promise<Product | null> => null;
  const updateProduct = async (): Promise<Product | null> => null;
  const deleteProduct = async (): Promise<void> => {};

  useEffect(() => {
    let isMounted = true;

    const restoreAuthSession = async () => {
      if (!authService.isAuthenticated()) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await authService.getCurrentUser();
        if (!isMounted) {
          return;
        }

        if (response.success && response.user) {
          setCurrentUser((previousUser) => mergeUserWithFallback(mapApiUserToShopUser(response.user), previousUser));
        }
      } catch (error: unknown) {
        if (!isMounted) {
          return;
        }

        const err = error as { message?: string };
        if (err.message?.toLowerCase().includes('unauthorized')) {
          authService.clearAuth();
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void restoreAuthSession();

    return () => {
      isMounted = false;
    };
  }, []);

  // Sync method - local storage based
  const loginBuyer = (email: string, password: string) => {
    void password;
    const normalizedEmail = normalizeEmail(email);
    const emailLocalPart = normalizedEmail.split('@')[0] ?? normalizedEmail;
    const persistedProfile =
      buyerProfiles[normalizedEmail] ??
      Object.values(buyerProfiles).find((profile) => {
        if (profile.role !== 'buyer') {
          return false;
        }

        const profileLocalPart = normalizeEmail(profile.email).split('@')[0];
        return profileLocalPart === emailLocalPart;
      });
    const buyerName = persistedProfile?.name ?? buildBuyerNameFromEmail(normalizedEmail);

    setCurrentUser({
      id: `buyer-${Date.now()}`,
      name: buyerName,
      email: normalizedEmail,
      role: 'buyer',
      provider: 'email',
      preferredLanguage: persistedProfile?.preferredLanguage ?? getPreferredLanguage(),
      phone: persistedProfile?.phone,
      country: persistedProfile?.country,
      dateOfBirth: persistedProfile?.dateOfBirth,
      profileImageUrl: persistedProfile?.profileImageUrl,
    });
  };

  // Async method - API based
  const loginBuyerAsync = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    setIsLoading(true);
    try {
      const response = await authService.login({ email: normalizeEmail(email), password });
      if (response.success) {
        if (response.user) {
          setCurrentUser((previousUser) => mergeUserWithFallback(mapApiUserToShopUser(response.user), previousUser));
          return { success: true };
        }

        if (!authService.isAuthenticated()) {
          return {
            success: false,
            message: 'Login response did not include token or user id. Please check backend login response payload.',
          };
        }

        const meResponse = await authService.getCurrentUser();
        if (meResponse.success && meResponse.user) {
          setCurrentUser((previousUser) => mergeUserWithFallback(mapApiUserToShopUser(meResponse.user), previousUser));
          return { success: true };
        }

        return { success: false, message: 'Login succeeded but no user profile was returned.' };
      }

      return { success: false, message: response.message || 'Login failed' };
    } catch (error: unknown) {
      const err = error as { status?: number; message?: string };
      const normalizedMessage = err.message?.toLowerCase() ?? '';

      if (err.status === 401 || normalizedMessage.includes('unauthorized') || normalizedMessage.includes('invalid credentials')) {
        return { success: false, message: 'Incorrect email or password. Please try again.' };
      }

      return { success: false, message: err.message || 'Login failed. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const loginVendor = (email: string, password: string) => {
    void password;
    const vendorName = buildBuyerNameFromEmail(email);

    setCurrentUser({
      id: `vendor-${Date.now()}`,
      name: vendorName,
      email,
      role: 'vendor',
      provider: 'email',
      preferredLanguage: getPreferredLanguage(),
    });
  };

  // Sync method - local storage based
  const registerBuyer = (payload: BuyerRegistrationInput) => {
    const {
      name,
      email,
      password,
      phoneCode,
      phoneNumber,
      country,
      preferredLanguage,
      dateOfBirth,
      profileImageUrl,
    } = payload;
    void password;
    const normalizedEmail = normalizeEmail(email);
    const nextBuyerUser: User = {
      id: `buyer-${Date.now()}`,
      name,
      email: normalizedEmail,
      role: 'buyer',
      provider: 'email',
      phone: `${phoneCode} ${phoneNumber}`.trim(),
      country,
      preferredLanguage,
      dateOfBirth,
      profileImageUrl,
    };

    setCurrentUser(nextBuyerUser);
    setBuyerProfiles((previousProfiles) => ({
      ...previousProfiles,
      [normalizedEmail]: nextBuyerUser,
    }));
  };

  // Async method - API based
  const registerBuyerAsync = async (payload: BuyerRegistrationInput): Promise<{ success: boolean; message?: string }> => {
    setIsLoading(true);
    try {
      const normalizedEmail = normalizeEmail(payload.email);
      const response = await authService.signup({
        username: buildUsernameFromEmail(normalizedEmail),
        fullname: payload.name,
        telephone: `${payload.phoneCode} ${payload.phoneNumber}`.trim(),
        email: normalizedEmail,
        password: payload.password,
        role: 'customer',
        audienceProfile: payload.audienceProfile ?? 'mixed',
        preferredLanguage: payload.preferredLanguage,
      });
      
      if (response.success) {
        if (response.user) {
          setCurrentUser((previousUser) => mergeUserWithFallback(mapApiUserToShopUser(response.user), previousUser));
          return { success: true };
        }

        if (!authService.isAuthenticated()) {
          return {
            success: false,
            message: 'Signup response did not include token or user id. Please check backend signup response payload.',
          };
        }

        const meResponse = await authService.getCurrentUser();
        if (meResponse.success && meResponse.user) {
          setCurrentUser((previousUser) => mergeUserWithFallback(mapApiUserToShopUser(meResponse.user), previousUser));
          return { success: true };
        }

        return { success: false, message: 'Registration succeeded but no user profile was returned.' };
      }
      return { success: false, message: response.message || 'Registration failed' };
    } catch (error: unknown) {
      const err = error as { message?: string };
      return { success: false, message: err.message || 'Registration failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithSocial = () => {
    setIsLoading(true);

    void (async () => {
      try {
        const googleSession = await signInWithGooglePopup();
        const response = await authService.loginWithGoogle({
          idToken: googleSession.idToken,
          token: googleSession.idToken,
        });

        if (response.success && response.user) {
          setCurrentUser((previousUser) => mergeUserWithFallback(mapApiUserToShopUser(response.user), previousUser));
          return;
        }

        if (authService.isAuthenticated()) {
          const meResponse = await authService.getCurrentUser();
          if (meResponse.success && meResponse.user) {
            setCurrentUser((previousUser) => mergeUserWithFallback(mapApiUserToShopUser(meResponse.user), previousUser));
            return;
          }
        }

        const fallbackEmail = normalizeEmail(googleSession.email || 'google-buyer@haapa.store');
        setCurrentUser({
          id: `buyer-${Date.now()}`,
          name: googleSession.displayName?.trim() || buildBuyerNameFromEmail(fallbackEmail),
          email: fallbackEmail,
          role: 'buyer',
          provider: 'google',
          preferredLanguage: getPreferredLanguage(),
        });
      } catch {
        // Keep the current auth state unchanged when popup/login is canceled or fails.
      } finally {
        setIsLoading(false);
      }
    })();
  };

  const loginAdmin = (email: string, password: string): boolean => {
    const isValidAdmin = email.toLowerCase() === ADMIN_EMAIL && password === ADMIN_PASSWORD;
    if (!isValidAdmin) {
      return false;
    }

    const adminUser: User = {
      id: 'admin-1',
      name: 'Store Admin',
      email: ADMIN_EMAIL,
      role: 'admin',
      provider: 'email',
      preferredLanguage: getPreferredLanguage(),
    };

    setCurrentUser(adminUser);
    const storedAdmin: UserData = {
      id: adminUser.id,
      name: adminUser.name,
      email: adminUser.email,
      role: adminUser.role,
      fullname: adminUser.name,
      preferredLanguage: adminUser.preferredLanguage,
      provider: adminUser.provider,
    };
    authService.setStoredUser(storedAdmin);

    return true;
  };

  // Sync method - local storage based
  const updateProfile = (updates: Partial<User>) => {
    setCurrentUser((previousUser) => {
      if (!previousUser) {
        return previousUser;
      }

      const nextUser: User = {
        ...previousUser,
        ...updates,
        id: previousUser.id,
        role: previousUser.role,
        provider: previousUser.provider,
      };

      if (nextUser.role === 'buyer') {
        const normalizedEmail = normalizeEmail(nextUser.email);
        setBuyerProfiles((previousProfiles) => ({
          ...previousProfiles,
          [normalizedEmail]: nextUser,
        }));
      }

      return nextUser;
    });
  };

  // Async method - API based
  const updateProfileAsync = async (updates: Partial<User>): Promise<{ success: boolean; message?: string }> => {
    if (!currentUser) {
      return { success: false, message: 'No authenticated user found.' };
    }

    setIsLoading(true);
    try {
      const resolvedDateOfBirth = updates.dateOfBirth ?? currentUser.dateOfBirth;
      const response = await authService.updateProfile({
        fullname: updates.name?.trim() || currentUser.name,
        name: updates.name?.trim() || currentUser.name,
        telephone: updates.phone?.trim() || currentUser.phone,
        phone: updates.phone?.trim() || currentUser.phone,
        audienceProfile: getAudienceProfileFromDateOfBirth(resolvedDateOfBirth),
        preferredLanguage: updates.preferredLanguage || currentUser.preferredLanguage,
        country: updates.country?.trim() || currentUser.country,
        dateOfBirth: resolvedDateOfBirth,
        profileImageUrl: updates.profileImageUrl || currentUser.profileImageUrl,
        file: updates.profileImageUrl || currentUser.profileImageUrl,
      });
      
      if (response.success) {
        const mergedUpdates: Partial<User> = {
          ...updates,
          name:
            response.user?.name?.trim() ||
            response.user?.fullname?.trim() ||
            updates.name ||
            currentUser.name,
          phone: response.user?.phone || response.user?.telephone || updates.phone || currentUser.phone,
          country: response.user?.country || updates.country || currentUser.country,
          dateOfBirth: response.user?.dateOfBirth || updates.dateOfBirth || currentUser.dateOfBirth,
          profileImageUrl: response.user?.profileImageUrl || updates.profileImageUrl || currentUser.profileImageUrl,
          preferredLanguage:
            response.user?.preferredLanguage || updates.preferredLanguage || currentUser.preferredLanguage,
        };

        updateProfile(mergedUpdates);
        return { success: true };
      }
      return { success: false, message: response.message || 'Update failed' };
    } catch (error: unknown) {
      const err = error as { message?: string };
      return { success: false, message: err.message || 'Update failed' };
    } finally {
      setIsLoading(false);
    }
  };

  // Sync method - local storage based
  const logout = () => {
    void authService.logout();
    setCurrentUser(null);
    setUsers([]);
    setCart([]);
  };

  // Async method - API based
  const logoutAsync = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } finally {
      setCurrentUser(null);
      setUsers([]);
      setCart([]);
      setIsLoading(false);
    }
  };

  const addToCart = (product: Product, quantity: number, options?: { edition?: 'digital' | 'print' }) => {
    if (!currentUser) {
      // Require authenticated user; do not keep any anonymous cart locally.
      return;
    }

    const normalizedQuantity = Math.max(1, Math.floor(quantity));

    setCart((previousCart) => {
      const existingItem = previousCart.find((cartItem) => cartItem.product.id === product.id);
      if (!existingItem) {
        return [...previousCart, { product, quantity: normalizedQuantity }];
      }

      return previousCart.map((cartItem) =>
        cartItem.product.id === product.id
          ? { ...cartItem, quantity: cartItem.quantity + normalizedQuantity }
          : cartItem,
      );
    });

    void (async () => {
      try {
        const apiItems = await cartService.addItem(product.id, normalizedQuantity, options);
        const normalized = mapCartItemsFromApi(apiItems);
        if (normalized.length > 0 || apiItems.length === 0) {
          setCart(normalized);
        }
      } catch (error) {
        console.error('Failed to sync add to cart', error);
      }
    })();
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    const normalizedQuantity = Math.max(1, Math.floor(quantity));
    setCart((previousCart) =>
      previousCart.map((cartItem) =>
        cartItem.product.id === productId ? { ...cartItem, quantity: normalizedQuantity } : cartItem,
      ),
    );

    void (async () => {
      try {
        const apiItems = await cartService.updateItem(productId, normalizedQuantity);
        const normalized = mapCartItemsFromApi(apiItems);
        if (normalized.length > 0 || apiItems.length === 0) {
          setCart(normalized);
        }
      } catch (error) {
        console.error('Failed to sync cart quantity', error);
      }
    })();
  };

  const removeFromCart = (productId: string) => {
    setCart((previousCart) => previousCart.filter((cartItem) => cartItem.product.id !== productId));

    void (async () => {
      try {
        const apiItems = await cartService.removeItem(productId);
        const normalized = mapCartItemsFromApi(apiItems);
        setCart(normalized);
      } catch (error) {
        console.error('Failed to sync cart removal', error);
      }
    })();
  };

  const placeOrder = async (payload: CheckoutPayload): Promise<Order> => {
    if (!currentUser) {
      throw new Error('Authentication is required to place an order.');
    }

    if (cart.length === 0) {
      throw new Error('Cart cannot be empty when placing an order.');
    }

    setIsLoading(true);
    try {
      // LEGACY FLOW FALLBACK
      const apiOrderPayload = await orderService.createCheckout(payload);
      const normalizedOrders = mapOrdersFromApi(apiOrderPayload);
      const createdOrder = normalizedOrders[0];

      if (createdOrder) {
        const hasPodItems = cart.some((item) => item.isPOD);
        const deliveryFeeRwf = hasPodItems ? 1500 : 0;
        const deliveryFeeUsd = hasPodItems ? 1.15 : 0;

        let linkedOrder: Order = {
          ...createdOrder,
          paymentMethod: payload.paymentMethod,
          podProjectId: payload.podProjectId ?? createdOrder.podProjectId,
          podOrderId: payload.podOrderId ?? createdOrder.podOrderId,
        };

        const resolvedSubtotal = linkedOrder.subtotal > 0 ? linkedOrder.subtotal : cartSubtotal;
        const orderCurrency = linkedOrder.currency || 'RWF';
        const orderDeliveryFee = orderCurrency === 'USD' ? deliveryFeeUsd : deliveryFeeRwf;

        linkedOrder = {
          ...linkedOrder,
          subtotal: resolvedSubtotal,
          shippingFee: orderDeliveryFee,
          total: resolvedSubtotal + orderDeliveryFee,
        };

        setOrders((previousOrders) => [linkedOrder, ...previousOrders.filter((order) => order.id !== linkedOrder.id)]);

        const shouldCreateIntent =
          payload.paymentMethod === 'card' &&
          !linkedOrder.paymentClientSecret &&
          !linkedOrder.paymentIntentId &&
          !linkedOrder.paymentReference &&
          !linkedOrder.paymentUrl;

        if (shouldCreateIntent) {
          try {
            const paymentIntent = await paymentService.createIntent({
              orderId: linkedOrder.id,
              amount: linkedOrder.total,
              currency: linkedOrder.currency ?? 'RWF',
              paymentMethod: payload.paymentMethod,
              podProjectId: linkedOrder.podProjectId,
              podOrderId: linkedOrder.podOrderId,
              metadata: {
                buyerId: currentUser.id,
              },
            });

            linkedOrder = {
              ...linkedOrder,
              paymentClientSecret: paymentIntent.clientSecret ?? linkedOrder.paymentClientSecret,
              paymentIntentId: paymentIntent.paymentIntentId ?? linkedOrder.paymentIntentId,
              paymentReference: paymentIntent.paymentReference ?? linkedOrder.paymentReference,
              paymentUrl: paymentIntent.paymentUrl ?? linkedOrder.paymentUrl,
              paymentStatus: paymentIntent.status ?? linkedOrder.paymentStatus ?? 'pending',
            };

            if (!linkedOrder.paymentReference && !linkedOrder.paymentUrl) {
              throw new Error('Payment intent was created without payment reference.');
            }

            setOrders((previousOrders) => [
              linkedOrder,
              ...previousOrders.filter((order) => order.id !== linkedOrder.id),
            ]);
          } catch (error: unknown) {
            const err = error as { message?: string };
            throw new Error(
              `Order ${linkedOrder.id} was created, but card payment setup failed. ${err.message ?? 'Please retry payment.'}`,
            );
          }
        } else if (payload.paymentMethod === 'cod' && !linkedOrder.paymentStatus) {
          linkedOrder = {
            ...linkedOrder,
            paymentStatus: 'pending',
          };

          setOrders((previousOrders) => [
            linkedOrder,
            ...previousOrders.filter((order) => order.id !== linkedOrder.id),
          ]);
        }

        await syncCartFromApi();
        return linkedOrder;
      }

      throw new Error('Checkout failed: backend did not return an order.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: string, nextStatus: OrderStatus): Promise<void> => {
    const previousOrders = orders;
    const optimisticOrders = orders.map((order) => {
      if (order.id !== orderId || order.status === nextStatus) {
        return order;
      }
      const statusTimestamp = new Date().toISOString();
      return {
        ...order,
        status: nextStatus,
        statusHistory: [...order.statusHistory, { status: nextStatus, timestamp: statusTimestamp }],
      };
    });
    setOrders(optimisticOrders);

    try {
      const toBackendStatus = (value: OrderStatus): string => {
        if (value === 'Processed') return 'processing';
        if (value === 'En Route') return 'en_route';
        if (value === 'Arrived') return 'delivered';
        return value.trim().toLowerCase();
      };

      const backendStatus = toBackendStatus(nextStatus);
      const payload =
        currentUser?.role === 'admin' || currentUser?.role === 'manager'
          ? await orderService.adminUpdateOrderStatus(orderId, { status: backendStatus })
          : await orderService.updateStatus(orderId, backendStatus);
      const normalized = mapOrdersFromApi(payload);
      if (normalized.length > 0) {
        setOrders((existing) => {
          const remaining = existing.filter((order) => order.id !== orderId);
          return [normalized[0], ...remaining];
        });
      }
    } catch (error) {
      console.error('Failed to sync order status', error);
      setOrders(previousOrders);
    }
  };

  const toggleTheme = () => {
    setThemeMode((previousThemeMode) => (previousThemeMode === 'dark' ? 'light' : 'dark'));
  };

  const toggleWishlist = (productId: string) => {
    setWishlist((prev) => 
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const isInWishlist = (productId: string) => wishlist.includes(productId);

  const directPodPayment = async (input: DirectPodPaymentInput): Promise<DirectPaymentResult> => {
    try {
      return await directPaymentService.directPodPayment(input.amount, input.productId);
    } catch (error) {
      console.error('Direct POD payment failed:', error);
      throw error;
    }
  };

  const directPodPaymentToFlutterwave = async (product: Product, podProjectId?: string): Promise<DirectPaymentResult> => {
    if (!currentUser) {
      throw new Error('Buyer authentication required for POD purchase.');
    }
    const amount = product.price;
    const input: DirectPodPaymentInput = {
      amount,
      productId: product.id,
      buyerEmail: currentUser.email,
      buyerName: currentUser.name,
      podProjectId,
      metadata: { productName: product.nameKey },
    };
    return directPodPayment(input);
  };

  const value: ShopContextValue = {
    currentUser,
    users,
    products,
    categories,
    cart,
    orders,
    wishlist,
    themeMode,
    cartSubtotal,
    cartTotalItems,
    isLoading,
    isCatalogLoading,
    isUsersLoading,
    refreshCart,
    refreshCatalog,
    refreshUsers,
    refreshOrders,
    createProduct,
    updateProduct,
    deleteProduct,
    loginBuyer,
    loginVendor,
    registerBuyer,
    loginWithSocial,
    loginAdmin,
    updateProfile,
    logout,
    loginBuyerAsync,
    registerBuyerAsync,
    logoutAsync,
    updateProfileAsync,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    placeOrder,
    directPodPayment,
    directPodPaymentToFlutterwave,
    updateOrderStatus,
    toggleTheme,
    toggleWishlist,
    isInWishlist,
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

export const useShop = (): ShopContextValue => {
  const context = useContext(ShopContext);
  return context ?? emptyContext;
};
